import React, { useState, useEffect } from 'react';
import AddVehicleModal from '../components/AddVehicleModal';
import UpdateVehicleModal from '../components/UpdateVehicleModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ToastContainer from '../components/ToastContainer';
import { apiService, Vehicle } from '../services/api';
import { exportVehiclesToCSV } from '../utils/csvExport';
import { Search, Download, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalVehicles: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const ManageVehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  
  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalVehicles: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [selectedVehicles, setSelectedVehicles] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery]);

  const loadVehicles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getVehicles({
        page: currentPage,
        limit: limit,
        search: searchQuery || undefined,
      });
      
      console.log('ManageVehicles - Vehicles response:', response);
      
      if (response.success && response.data) {
        const serverResponse = response.data as any;
        
        // Server response structure: { success, message, data: [...], pagination: {...} }
        let vehiclesArray: Vehicle[] = [];
        let paginationData: PaginationInfo | null = null;
        
        // Check if serverResponse.data exists (vehicles array)
        if (serverResponse.data && Array.isArray(serverResponse.data)) {
          vehiclesArray = serverResponse.data;
          paginationData = serverResponse.pagination;
        } else if (Array.isArray(serverResponse)) {
          // Fallback: if response.data is directly an array
          vehiclesArray = serverResponse;
        }
        
        // Handle pagination
        if (serverResponse.pagination) {
          paginationData = serverResponse.pagination;
        } else if (response.pagination) {
          paginationData = response.pagination as any;
        }
        
        console.log('ManageVehicles - Vehicles array:', vehiclesArray);
        console.log('ManageVehicles - Pagination:', paginationData);
        
        setVehicles(vehiclesArray);
        if (paginationData) {
          setPagination(paginationData);
        } else {
          // Default pagination if not provided
          setPagination({
            currentPage: currentPage,
            totalPages: Math.ceil(vehiclesArray.length / limit),
            totalVehicles: vehiclesArray.length,
            hasNextPage: false,
            hasPrevPage: currentPage > 1,
          });
        }
      } else {
        setError(response.error || 'Failed to load vehicles');
        setVehicles([]);
      }
    } catch (error) {
      console.error('ManageVehicles - Error loading vehicles:', error);
      setError('Failed to load vehicles');
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVehicle = (newVehicle: Vehicle) => {
    console.log('ManageVehicles - New vehicle added:', newVehicle);
    addToast('Vehicle added successfully', 'success');
    loadVehicles();
  };

  const handleUpdateVehicle = (updatedVehicle: Vehicle) => {
    console.log('ManageVehicles - Vehicle updated:', updatedVehicle);
    addToast('Vehicle updated successfully', 'success');
    loadVehicles();
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsUpdateModalOpen(true);
  };

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleDeleteClick = (id: string) => {
    setVehicleToDelete(id);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (isBulkDelete) {
      // Bulk delete
      const vehicleIds = Array.from(selectedVehicles);
      if (vehicleIds.length === 0) {
        setShowConfirmModal(false);
        setIsBulkDelete(false);
        return;
      }

      try {
        const response = await apiService.bulkDeleteVehicles(vehicleIds);
        if (response.success) {
          const deletedCount = response.data?.deletedCount || vehicleIds.length;
          addToast(`${deletedCount} vehicle(s) deleted successfully`, 'success');
          setSelectedVehicles(new Set());
          loadVehicles();
        } else {
          addToast(response.error || 'Failed to delete vehicles', 'error');
        }
      } catch (error) {
        addToast('An unexpected error occurred while deleting vehicles', 'error');
      } finally {
        setShowConfirmModal(false);
        setIsBulkDelete(false);
      }
    } else {
      // Single delete
      if (!vehicleToDelete) return;

      try {
        const response = await apiService.deleteVehicle(vehicleToDelete);
        if (response.success) {
          addToast('Vehicle deleted successfully', 'success');
          loadVehicles();
        } else {
          addToast(response.error || 'Failed to delete vehicle', 'error');
        }
      } catch (error) {
        addToast('An unexpected error occurred while deleting the vehicle', 'error');
      } finally {
        setShowConfirmModal(false);
        setVehicleToDelete(null);
      }
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedVehicles.size === 0) {
      addToast('Please select at least one vehicle to delete', 'warning');
      return;
    }
    setIsBulkDelete(true);
    setShowConfirmModal(true);
  };

  const handleDeleteCancel = () => {
    setShowConfirmModal(false);
    setVehicleToDelete(null);
    setIsBulkDelete(false);
  };

  const handleExportVehicles = () => {
    if (vehicles.length === 0) {
      addToast('No vehicles to export', 'warning');
      return;
    }
    exportVehiclesToCSV(vehicles);
    addToast('Vehicles exported successfully', 'success');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = vehicles.map(v => v._id).filter(Boolean) as string[];
      setSelectedVehicles(new Set(allIds));
    } else {
      setSelectedVehicles(new Set());
    }
  };

  const handleSelectVehicle = (vehicleId: string) => {
    setSelectedVehicles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  const isAllSelected = vehicles.length > 0 && vehicles.every(v => v._id && selectedVehicles.has(v._id));

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-full">
      {/* Page Title and Description */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Vehicles</h1>
        <p className="text-gray-600">View, manage, and track vehicles performance and availability.</p>
      </div>

      {/* Search and Action Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by vehicle no, name"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-jd-green"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {selectedVehicles.size > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedVehicles.size})
            </button>
          )}
          {/* <button
            onClick={() => {}}
            className="inline-flex items-center px-4 py-2 border border-jd-green text-jd-green rounded-lg hover:bg-jd-green hover:text-white transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button> */}
          <button
            onClick={handleExportVehicles}
            className="inline-flex items-center px-4 py-2 border border-jd-green text-jd-green rounded-lg hover:bg-jd-green hover:text-white transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <span className="mr-2">+</span>
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500">Loading vehicles...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={loadVehicles}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Vehicle Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {!Array.isArray(vehicles) || vehicles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No vehicles found. Add your first vehicle to get started.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-jd-green border-gray-300 rounded focus:ring-jd-green"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        Vehicle No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        RC No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        Assigned Driver
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-black-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicles.map((vehicle, index) => {
                      const isSelected = vehicle._id ? selectedVehicles.has(vehicle._id) : false;
                      return (
                        <tr key={vehicle._id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => vehicle._id && handleSelectVehicle(vehicle._id)}
                              className="w-4 h-4 text-jd-green border-gray-300 rounded focus:ring-jd-green"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {vehicle.vehicleNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {vehicle.vehicleType}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {vehicle.vehicleRCNumber || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {vehicle.assignedDriver || 'Not Assigned'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              vehicle.status === 'Active' || !vehicle.status
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {vehicle.status || 'Active'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleEditVehicle(vehicle)}
                              className="text-blue-600 hover:text-blue-800 mr-4 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => vehicle._id && handleDeleteClick(vehicle._id)}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * limit, pagination.totalVehicles)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalVehicles}</span> vehicles
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === pagination.totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                page === currentPage
                                  ? 'bg-jd-green text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-2 text-gray-500">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add Vehicle Modal */}
      <AddVehicleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddVehicle={handleAddVehicle}
      />

      <UpdateVehicleModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedVehicle(null);
        }}
        onUpdateVehicle={handleUpdateVehicle}
        vehicle={selectedVehicle}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={isBulkDelete ? "Delete Selected Vehicles" : "Delete Vehicle"}
        message={isBulkDelete 
          ? `Are you sure you want to delete ${selectedVehicles.size} selected vehicle(s)? This action cannot be undone.`
          : "Are you sure you want to delete this vehicle? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Container */}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </div>
  );
};

export default ManageVehicles;
